import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const IL_TLDS = ['.co.il', '.org.il', '.net.il', '.ac.il', '.muni.il'];

function isIlDomain(domain) {
  const lower = (domain || '').toLowerCase();
  return IL_TLDS.some(tld => lower.endsWith(tld));
}

const EMPTY_CONTACT = {
  nameFirst: '',
  nameLast: '',
  email: '',
  phone: '',
  organization: '',
  address1: '',
  city: '',
  postalCode: '',
  country: 'IL',
  state: '',
};

export default function DomainPurchaseRetryModal({ purchase, onClose, onSuccess }) {
  const [contact, setContact] = useState({ ...EMPTY_CONTACT });
  const [idNumber, setIdNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const ilDomain = isIlDomain(purchase?.domain);

  useEffect(() => {
    const saved = purchase?.contact_info || {};
    setContact({
      ...EMPTY_CONTACT,
      nameFirst: saved.nameFirst || '',
      nameLast: saved.nameLast || '',
      email: saved.email || '',
      phone: saved.phone || '',
      organization: saved.organization || '',
      address1: saved.address1 || '',
      city: saved.city || '',
      postalCode: saved.postalCode || '',
      country: saved.country || (ilDomain ? 'IL' : ''),
      state: saved.state || '',
    });
    setIdNumber('');
    setErrors({});
  }, [purchase, ilDomain]);

  const updateField = (field, value) => {
    setContact(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!contact.nameFirst.trim()) next.nameFirst = 'Required';
    if (!contact.nameLast.trim()) next.nameLast = 'Required';
    if (!contact.email.trim()) next.email = 'Required';
    if (!contact.phone.trim()) next.phone = 'Required';
    if (!contact.address1.trim()) next.address1 = 'Required';
    else if (ilDomain && !/\d/.test(contact.address1.trim())) {
      next.address1 = 'Must include a street number';
    }
    if (!contact.city.trim()) next.city = 'Required';
    if (!contact.postalCode.trim()) next.postalCode = 'Required';
    else if (ilDomain && !/^\d{7}$/.test(contact.postalCode.trim())) {
      next.postalCode = 'Must be 7 digits';
    }
    if (!contact.country.trim()) next.country = 'Required';
    if (ilDomain) {
      const digits = String(idNumber).replace(/[^0-9]/g, '');
      if (!digits) next.idNumber = 'Required for .co.il registration';
      else if (digits.length < 8 || digits.length > 9) next.idNumber = 'Must be 8–9 digits';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/domain-purchases/${purchase.id}/retry-registration`,
        {
          contact,
          ...(ilDomain && { idNumber }),
        }
      );
      toast.success('Domain registration retry started');
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || err.message
        || 'Retry failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (field, label, opts = {}) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={opts.type || 'text'}
        value={contact[field]}
        onChange={(e) => updateField(field, e.target.value)}
        className={`w-full border rounded-lg px-3 py-2 text-sm ${errors[field] ? 'border-red-400' : 'border-gray-300'}`}
      />
      {errors[field] && <p className="text-xs text-red-600 mt-1">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Retry domain registration</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {purchase?.domain} — no new charge (payment already collected)
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Edit registrant details and submit again. For Israeli domains, enter the customer&apos;s
            ID or company registration number — it is sent to LiveDNS only and is not saved in Zappy.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInput('nameFirst', 'First name')}
            {renderInput('nameLast', 'Last name')}
            {renderInput('email', 'Email', { type: 'email' })}
            {renderInput('phone', 'Phone')}
            {renderInput('organization', 'Organization (optional)')}
            {renderInput('address1', 'Address')}
            {renderInput('city', 'City')}
            {renderInput('postalCode', 'Postal code')}
            {renderInput('country', 'Country (ISO code, e.g. IL)')}
          </div>

          {ilDomain && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <label className="block text-sm font-semibold text-amber-900 mb-1">
                Israeli ID / company registration number *
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={idNumber}
                onChange={(e) => {
                  setIdNumber(e.target.value);
                  setErrors(prev => ({ ...prev, idNumber: undefined }));
                }}
                placeholder="8–9 digits (Teudat Zehut or ח.פ.)"
                className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${errors.idNumber ? 'border-red-400' : 'border-amber-300'}`}
              />
              {errors.idNumber && <p className="text-xs text-red-600 mt-1">{errors.idNumber}</p>}
              <p className="text-xs text-amber-800 mt-2">
                Not stored — required by LiveDNS for each registration attempt.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-60"
            >
              {submitting && <LoadingSpinner size="small" />}
              Retry registration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
